import type { JSX } from '@builder.io/qwik';
import { component$, useContext } from '@builder.io/qwik';
import { cva } from 'class-variance-authority';
import { ArticleContext } from '~/contexts/ArticleContext';
import { useIsPremiumTemplate } from '~/utilities/useIsPremiumTemplate';
import { AppDataContext } from '~/contexts/AppDataContext';
import { hasFanSidedVideo } from '~/utilities/experiments/fanSidedVideoAds';

const cssClasses = cva(
  'player-wrapper relative aspect-video mb-[18px] bg-grey rounded-md',
  {
    variants: {
      size: {
        // TODO: Not sure how these player sizes are used
        sm: 'player-wrapper-sm md:hidden block',
        md: 'player-wrapper-sm md:hidden block',
        lg: 'hidden player-wrapper-lg md:block',
      },
      isPremium: {
        true: 'my-[30px]',
      },
    },
  }
);

type VideoPlayerType = {
  sizes?: string[];
  mediaValue: undefined | { version: string; html: string };
  vidId?: string;
  isTemplate?: boolean;
};

export default component$<VideoPlayerType>(
  ({ sizes, mediaValue, vidId, isTemplate }): JSX.Element => {
    const article = useContext(ArticleContext);
    const appData = useContext(AppDataContext);

    if (!hasFanSidedVideo(appData.property)) return null;

    // TODO: Remove once the desktop video placement experiment ends
    // If it's fansided.com mobile, we are moving the video player to after the first paragraph, so don't include the template video
    if (
      isTemplate &&
      appData.payloadToSSR.edition === '229' &&
      appData.payloadToSSR.isMobileViewer
    )
      return <></>;

    if (!article) return <></>;
    const noVideo =
      article.metadata?.commercialTags?.includes('no-video-player');

    if ((article.finiteScrollNumber ?? 0) > 0 || noVideo) return <></>;

    const isPremium = useIsPremiumTemplate();

    const defaultId = 'mm-player-placeholder';
    let divId = `${defaultId}-small-screen`;

    if (sizes?.includes('sm') && sizes.includes('md')) {
      divId = `${defaultId}-smallAndMedium-screens`;
    } else if (sizes?.includes('lg')) {
      divId = `${defaultId}-large-screen`;
    }

    return (
      <figure
        class={cssClasses({
          size: sizes?.includes('sm') || sizes?.includes('md') ? 'sm' : 'lg',
          isPremium,
        })}
      >
        <div
          class='absolute top-0 left-0 w-full h-full'
          id={vidId ?? divId}
          dangerouslySetInnerHTML={mediaValue?.html}
        />
      </figure>
    );
  }
);
